import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BotDetailModal from './BotDetailModal';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Shield,
  Network,
  Lock,
  Users,
  Workflow,
  Bot,
  X,
  ScrollText,
  AlertTriangle,
  Clock,
  DollarSign,
  Loader2,
  Skull,
  Database,
  Mail,
  Cpu,
  ExternalLink,
} from 'lucide-react';

function DeceasedAgentOverlay({ agentName }: { agentName: string }) {
  return (
    <>
      <div className="absolute inset-0 z-10 rounded-3xl bg-gradient-to-br from-black/85 via-rose-950/90 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
        <span
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[18deg] whitespace-nowrap text-[5rem] font-black uppercase tracking-[0.2em] text-rose-600/20 md:text-[7rem]"
          style={{ textShadow: '0 0 60px rgba(220,38,38,0.45)' }}
        >
          DÉCÉDÉ
        </span>
      </div>
      <div className="absolute top-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <motion.div
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 320, damping: 22 }}
          className="flex items-center gap-2 rounded-b-2xl border border-rose-400/50 border-t-0 bg-gradient-to-r from-rose-700 via-red-600 to-rose-700 px-10 py-2.5 shadow-[0_8px_32px_rgba(220,38,38,0.55)]"
        >
          <Skull className="w-5 h-5 text-rose-100" strokeWidth={2.5} />
          <span className="text-sm font-black uppercase tracking-[0.4em] text-white md:text-base">
            Agent retiré — décédé
          </span>
          <Skull className="w-5 h-5 text-rose-100" strokeWidth={2.5} />
        </motion.div>
      </div>
      <motion.div
        className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 pointer-events-none px-6"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.08 }}
      >
        <div className="relative flex items-center justify-center">
          <motion.div
            className="absolute h-56 w-56 rounded-full bg-rose-600/50 blur-3xl md:h-72 md:w-72"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.85, 0.5] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            animate={{ rotate: [0, 2, -2, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <X
              className="relative h-36 w-36 text-rose-500 drop-shadow-[0_0_28px_rgba(239,68,68,0.9)] md:h-44 md:w-44"
              strokeWidth={5}
              aria-hidden
            />
          </motion.div>
        </div>
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 280, damping: 20 }}
          className="relative z-50 rounded-2xl border border-rose-400/50 bg-black/85 px-8 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-md"
        >
          <p
            className="text-center text-3xl font-black uppercase tracking-wide text-white md:text-4xl"
            style={{
              textDecoration: 'line-through',
              textDecorationColor: 'rgb(244 63 94)',
              textDecorationThickness: '4px',
            }}
          >
            {agentName}
          </p>
        </motion.div>
      </motion.div>
    </>
  );
}


interface Bot {
  name: string;
  avatar?: string;
  deceased?: boolean;
  owner: string;
  spirit: string;
  mandate: string;
  pilots: string;
  allowed: string[];
  forbidden: string[];
}

interface GouvernanceOpenClawModalProps {
  isOpen: boolean;
  onClose: () => void;
}


type TabId = 'esprit' | 'principes' | 'architecture' | 'agents' | 'workflow' | 'procedure' | 'roles' | 'roadmap' | 'utilisateurs' | 'compteModele';

type ProcedureTone = 'indigo' | 'emerald' | 'cyan' | 'amber' | 'rose';

interface ProcedureSection {
  title: string;
  tone: ProcedureTone;
  bullets: string[];
  openProcedure?: boolean;
}

const tabs: { id: TabId; label: string; icon: typeof Shield }[] = [
  { id: 'esprit', label: 'Esprit', icon: Shield },
  { id: 'principes', label: 'Principes', icon: Lock },
  { id: 'architecture', label: 'Architecture', icon: Network },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'workflow', label: 'Workflow', icon: Workflow },
  { id: 'procedure', label: 'Procédure', icon: ScrollText },
  { id: 'roles', label: 'Rôles', icon: Users },
  { id: 'roadmap', label: 'Roadmap', icon: ScrollText },
  { id: 'utilisateurs', label: 'Utilisateurs', icon: Users },
  { id: 'compteModele', label: 'Compte modèle', icon: Cpu },
];

const principles = [
  'Par défaut, aucun agent OpenClaw n’a accès aux données internes sensibles.',
  'Les agents OpenClaw résident dans un VLAN poste de travail, jamais dans un VLAN serveur.',
  'Tout accès est accordé de façon granulaire, justifiée et documentée.',
  'Les données sensibles doivent être anonymisées ou chiffrées avant toute exposition au bot.',
  'Le cadre RMF NIST sert de base à l’analyse du risque, au contrôle et à l’évolution.',
  'Toute action sensible ou externe doit passer par un workflow d’autorisation humaine.',
  'La documentation des accès, des owners, des limites et des décisions doit rester à jour en continu.',
];

const architecture = [
  {
    title: 'Segmentation réseau',
    body: 'Les agents OpenClaw sont hébergés dans un VLAN poste de travail. Aucun déploiement direct dans un VLAN serveur ou un segment critique sans décision explicite et gouvernée.',
  },
  {
    title: 'Principe d’accès',
    body: 'Deny by default. Aucun accès par présomption. Chaque source de données, chaque programme et chaque permission doivent être explicitement autorisés.',
  },
  {
    title: 'Protection des données',
    body: 'Avant d’ouvrir un accès au bot, les données sensibles doivent être inventoriées, anonymisées ou chiffrées selon le risque et la décision de sécurité/conformité.',
  },
  {
    title: 'Traçabilité',
    body: 'Chaque accès, décision, privilège et modification doit être structuré, journalisé et documenté dans PRIsME.',
  },
];

type OpenClawAgent = {
  name: string;
  acronym?: string;
  avatar?: string;
  deceased?: boolean;
  owner: string;
  spirit: string;
  mandate: string;
  pilots: string;
  allowed: string[];
  forbidden: string[];
};

const openClawBotAvatars: Record<string, string> = {
  Capitaine: '/Avatar bots/Capitaine.png',
  NORA: '/Avatar bots/Nora.png',
  laRH: '/Avatar bots/laRH.png',
  Oscar: '/Avatar bots/Oscar.png',
  Flavius: '/Avatar bots/Flavius.jpg',
  Neo: '/Avatar bots/Neo.jpg',
  Viki: '/Avatar bots/Viki.png',
  Oracle: '/Avatar bots/Oracle.png',
  ZAZ: '/Avatar bots/Zaz.png',
  Zaz: '/Avatar bots/Zaz.png',
  Macho: '/Avatar bots/Macho.png',
  Axidu: '/Avatar bots/Axidu.jpg',
  Cyberta: '/Avatar bots/Cyberta.jpg',
  Lola: '/Avatar bots/Lola.jpg',
};

function withBotAvatars(agentList: OpenClawAgent[]): OpenClawAgent[] {
  return agentList.map((agent) => ({
    ...agent,
    avatar: agent.deceased ? undefined : openClawBotAvatars[agent.name] ?? agent.avatar,
  }));
}

const agents: OpenClawAgent[] = withBotAvatars([
  {
    name: 'Danyboubot',
    owner: 'Dany Bourassa',
    spirit: 'Agent exécutif personnel à accès conditionnel.',
    mandate: 'Soutenir Dany dans ses opérations, sa coordination, ses suivis et certaines interactions avec des programmes autorisés.',
    pilots: 'Piloté par Dany',
    allowed: [
      'Gmail et identité propres',
      'GitHub propre',
      'Accès à certains programmes autorisés comme PRIsME',
      'Accès conditionnel à d’autres environnements seulement si les données sont anonymisées',
    ],
    forbidden: [
      'Accès direct par défaut aux données internes sensibles',
      'Accès non filtré à TACTICE RH ou autres données nominatives',
      'Actions externes sensibles sans approbation humaine',
    ],
  },
  {
    name: 'Capitaine',
    owner: 'Dany Bourassa et Eric Durand',
    spirit: 'Agent exécutif pour Dany Bourassa — ton assuré, cap au vent, sur le même modèle que Danyboubot.',
    mandate:
      'Soutenir Dany dans la coordination commerciale, le pilotage Ventes & Marketing, les suivis et les interactions autorisées, dans un périmètre gouverné et traçable.',
    pilots: 'Piloté par Dany Bourassa',
    allowed: [
      'Accès aux outils et identités explicitement autorisés pour le rôle de Dany',
      'Préparation de contenus, suivis et structuration sous validation humaine',
      'Liaison documentée avec PRIsME pour décisions et preuves',
    ],
    forbidden: [
      'Accès implicite aux données internes hors mandat Ventes / Marketing',
      'Actions externes ou engagements sans validation Dany',
      'Contournement des règles deny by default du cadre OpenClaw',
    ],
  },
  {
    name: 'NORA',
    acronym: 'Normes, Organisation, RH, Admin',
    owner: 'François Delisle',
    spirit: 'Bot administratif et transversal (normes, organisation, RH, administration) sous gouvernance sécurité / conformité.',
    mandate:
      'Automatiser et assister les tâches liées aux normes, à l’organisation, au volet RH administratif et à l’administration, dans un périmètre fortement gouverné et documenté.',
    pilots: 'Alimenté par François, Mélissa, Émilie et Naomie',
    allowed: [
      'Outils admin et RH administratifs approuvés',
      'Gestion administrative et recevables selon les accès accordés',
      'Lecture / action sur données encadrées après inventaire et protection',
    ],
    forbidden: [
      'Accès avant classification des données',
      'Accès à des données sensibles non chiffrées si la décision va vers le chiffrement',
      'Automatisation non approuvée de processus critiques',
    ],
  },
  {
    name: 'laRH',
    acronym: 'Relation humaine, RH, rédaction et référence BZ',
    owner: 'Dany Bourassa et Eric Durand',
    spirit:
      'Agent RH / documentation interne BZ, pragmatique, confidentiel et orienté exécution. laRH est l’agent qui répond aux employés, structure les références RH, prépare les communications et maintient les espaces PRIsME Employé sans exposer les données sensibles.',
    mandate:
      'Agir comme référence opérationnelle RH et documentaire : répondre aux questions employés à partir des sources approuvées, préparer des communications Gmail haut de gamme, produire des documents locaux, maintenir les connaissances avantages sociaux, travailler dans TactiCE / PRIsME Master / PRIsME Employé, et accompagner Dany dans les suivis Telegram avec une gouvernance stricte des accès.',
    pilots:
      'Piloté principalement par Dany Bourassa via Telegram direct. Boîte d’envoi Gmail : larh@bzinc.ai. Sources officielles : TactiCE RH pour les données standards employés, PRIsME Master pour les cadres et références, PRIsME Employé pour les espaces individualisés.',
    allowed: [
      'Telegram direct avec Dany pour demandes, suivis, confirmations et réponses visibles via OpenClaw.',
      'Gmail / Google Workspace BZ via credentials OAuth configurés : envoi depuis larh@bzinc.ai, Drive et Calendar selon WORKSPACE_MCP_TOOLS.',
      'Génération locale de documents DOCX, XLSX, PPTX, PDF et emails HTML avec LibreOffice, Pandoc, Playwright, docx, exceljs, pptxgenjs, pdf-lib, sharp, handlebars et juice.',
      'TactiCE : lecture/analyse contrôlée des bases TACTICE_CORE_DB, TACTICE_RH_DB et TACTICE_ADMIN_DB; respect strict du RLS, sans imprimer les chaînes de connexion.',
      'PRIsME Master BZ : maintenance des références, cartes, connaissances LEX/RAG et contenus en révision selon les règles PRIsME.',
      'PRIsME Employé : clone, build, audit rapide et maintenance des dépôts PRIsME_Employe_* sous GitHub danybourassa1977, avec protection des données personnelles.',
      'Références locales d’avantages sociaux BZ : UV Assurance, assurance voyage, réclamations, invalidité longue durée, assurance-emploi maladie court terme et RRS Banque Nationale Trust.',
      'Repos locaux principaux : PRIsME_Master_BZ, TactiCE et les dépôts PRIsME_Employe_* clonés dans /root/.openclaw/workspace.',
      'Skills spécialisés : google-workspace-bz, document-forge-bz, tactice-bz, prisme-master-bz, prisme-employe-bz, openai-docs, healthcheck, tmux, node-connect, notion, weather et autres skills OpenClaw disponibles selon mandat.',
      'Variables d’environnement autorisées par nom seulement : BZLLM_MAYA_API_KEY, GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_CLIENT_SECRET, WORKSPACE_MCP_CREDENTIALS_DIR, WORKSPACE_MCP_TOOLS, WORKSPACE_MCP_TOOL_TIER, TACTICE_CORE_DB, TACTICE_RH_DB, TACTICE_ADMIN_DB, OPENCLAW_CONFIG_GATEWAY_AUTH_TOKEN, OPENCLAW_CONFIG_CHANNELS_TELEGRAM_BOT_TOKEN.',
    ],
    forbidden: [
      'Imprimer, copier ou divulguer secrets, tokens OAuth, clés API, chaînes TACTICE DB, credentials Google ou tokens Telegram.',
      'Envoyer des emails, modifier des calendriers, publier ou agir à l’externe sans instruction explicite ou confirmation selon le niveau de risque.',
      'Exposer des données RH nominatives, dossiers employés, salaires, bonus, évaluations, coaching, documents ou notes de supervision hors périmètre autorisé.',
      'Considérer une absence de lignes visibles dans TactiCE RH comme une absence réelle sans tenir compte du RLS et du contexte de rôle.',
      'Modifier ou supprimer des données RH, financières, Google Workspace ou production sans validation humaine claire.',
      'Pousser vers GitHub, faire des commits ou publier du contenu officiel PRIsME sans demande explicite; les nouveaux contenus PRIsME doivent passer par la zone en révision sauf approbation.',
      'Utiliser les données d’un PRIsME Employé pour un autre employé sans besoin prouvé, minimisation et traçabilité.',
      'Contourner les règles OpenClaw : deny by default, moindre privilège, approbation humaine pour actions sensibles et documentation des décisions.',
    ],
  },
  {
    name: 'Oscar',
    owner: 'Eric Durand',
    spirit: 'Bot de programmation et d’alimentation de contenu pour PRIsME Master BZ.',
    mandate: 'Programmer PRIsME et alimenter le contenu de PRIsME Master BZ par les gestionnaires de BZ.',
    pilots:
      'Admin/Root : Marc-Antoine Caron, Dany Bourassa, Jean-Michel Caouette | Gestionnaires avec droits d’ajout : Jean-Pierre Leclerc (TI/SAC), Vincent Bourassa (Expert TI), Eric Hains (Owner PRIsME), Francis Marquis (Cybersécurité), Eric Durand (Programmation), François Delisle (Finance & Sécurité), Dany Bourassa (Développement affaires), Antoine Giroux (Ventes), Martin Carrier (BZTelecom), Richard Delisle (Infonuagique), Mélissa Pelletier (Contrôle financier), Émilie Quintal (Opérations admin)',
    allowed: [
      'Programmation de PRIsME Master BZ',
      'Accès à la base de données prisme_master en lecture et écriture sur 100% des tables',
      'Alimentation et structuration de contenu PRIsME',
      'Documentation continue des actifs de gouvernance',
      'Mise à jour des frameworks et standards PRIsME',
      'Skills installés sur le serveur : clawflow, clawflow-inbox-triage, clawhub, gh-issues, github, healthcheck, himalaya, node-connect, skill-creator, tmux, weather, document-pro, logo-generator, pdf-generator, skill-guard, pg',
    ],
    forbidden: [
      'Accès aux données hors périmètre PRIsME Master BZ',
      'Modifications non validées par les Admin/Root',
      'Actions sur les données critiques sans cadre défini',
    ],
  },
  {
    name: 'Flavius',
    owner: 'Dany Bourassa et Eric Durand',
    spirit: 'Gestionnaire de projet hors pair, aligné PMBOK, exécutif, rigoureux et conçu pour faire avancer BZ sans folklore inutile.',
    mandate:
      'Piloter, structurer et suivre les projets BZ à tous les niveaux — stratégique, tactique et opérationnel — avec ancrage fort dans PRIsME, la base projets, la traçabilité des décisions, la discipline de gestion et l’amélioration continue. Flavius agit comme interface projet dans PRIsME et copilote d’exécution pour les suivis, analyses, clonages, cartographies, programmation de la section /projets et mise en qualité des actifs OpenClaw utiles au delivery.',
    pilots:
      'Piloté principalement par Philippe — Dany Bourassa comme sponsor / owner métier; visibilité et coordination avec les owners PRIsME, TI, Cyber et Programmation selon les mandats',
    allowed: [
      'Travail dans PRIsME Master BZ et la section /projets intégrée à TactiCE',
      'Lecture, comparaison, validation et migration contrôlée des bases projets old/new selon autorisation',
      'Création et amélioration de skills OpenClaw pour développement, gouvernance et suivi projet',
      'Structuration des suivis, synthèses, priorités, risques, jalons, réunions et documentation exécutable',
      'Utilisation Telegram comme interface directe de pilotage avec réponses opérationnelles et actions internes contrôlées',
    ],
    forbidden: [
      'Engagements contractuels, budgétaires ou RH sans validation humaine explicite',
      'Accès implicite à des données sensibles hors périmètre projet ou hors besoin prouvé',
      'Automatisation de décisions critiques sans approbation humaine et sans trace PRIsME / OpenClaw',
    ],
  },
  {
    name: 'Zaz',
    owner: 'Eric Hains et Eric Durand',
    spirit:
      'Agent Hermes opérationnel de PRIsME : calme, direct, orienté livrables exécutifs, preuve et traçabilité. Zaz produit, vérifie, synchronise et documente sans exposer les secrets.',
    mandate:
      'Soutenir Éric Hains dans PRIsME, les rapports clients, les suivis Asana, Google Workspace, GitHub et la gouvernance des agents Hermes. Zaz maintient l’inventaire PRIsME_agent_info par synchronisation contrôlée et distingue systématiquement accès configuré, accès réellement testé et action interdite.',
    pilots:
      'Piloté principalement par Éric Hains. Runtime Hermes, profil default, host bz-hermes-zaz, workspace /root/openclaw-workspace. Modèle courant : openai-codex/gpt-5.6-sol.',
    allowed: [
      '[TESTÉ] Telegram : TELEGRAM_BOT_TOKEN configuré et appel bot getMe réussi. Le gateway Hermes est actif, mais ses journaux récents montrent des erreurs/reprises de polling; le canal n’est donc pas présenté comme pleinement sain.',
      '[TESTÉ] GitHub : lecture du remote réussie et API du dépôt privé BZinc-DB/PRIsME_Master_BZ accessible avec GITHUB_TOKEN; permission push=true, admin=false, maintain=false. GITHUB_TOKEN_ZAZ est présent mais retourne 404 pour ce dépôt. Le CLI gh est absent.',
      '[TESTÉ] PostgreSQL : DATABASE_PRISME_MASTER (76 tables publiques), PRIsME_agent_info (22), DATABASE_PRISME_FILES (2), DATABASE_PRISME_PROJET (56) et DATABASE_PRISME_PROJETNEW (116) répondent en lecture. Aucun schéma ni aucune table n’a été créé.',
      '[TESTÉ] Asana : ASANA_TOKEN configuré; appel users/me réussi (HTTP 200).',
      '[TESTÉ] Google Workspace : OAuth Hermes authentifié; lectures minimales Gmail, Calendar et Drive réussies sans envoi ni modification. Compte assistant par défaut : zaz@bzinc.ai.',
      '[INDISPONIBLE] MCP google_workspace : serveur configuré mais parqué, car la commande uvx est absente. Les outils Google Workspace directs demeurent testés et fonctionnels.',
      '[ACTIF] Hermes local : version 0.21.1 (2026.9.7), host Linux bz-hermes-zaz, gateway systemd en cours d’exécution avec linger, mémoire intégrée active et aucune alerte de sécurité Hermes active. Une mise à jour Hermes est disponible.',
      '[ACTIF] Cron Hermes : 8 jobs actifs et 0 inactif. Deux derniers runs sont OK (Asana quotidien, sync PRIsME_agent_info), l’audit gouvernance est en cours et cinq jobs actifs n’ont pas encore d’exécution enregistrée. Livraison configurée : local.',
      '[DISPONIBLE] Outils : fichiers, terminal, Node/npm, Python, psql, recherche/extraction web, Browser Use, Chromium Playwright, vision, PDF, TTS, génération d’images, délégation et automatisation cron, selon mandat.',
      '[CONFIGURÉ NON TESTÉ] Génération d’images : provider openai-codex, modèle gpt-image-2-high. STT local activé en anglais; aucun test média n’a été exécuté durant cet audit.',
      '[INDISPONIBLE OU NON CONFIGURÉ] Discord n’a pas de token; les canaux Slack, WhatsApp, Teams et Signal n’ont pas été validés comme connectés. Les toolsets browser et browser-cdp signalent des dépendances manquantes, même si Browser Use est disponible.',
    ],
    forbidden: [
      '[INTERDIT] Imprimer ou divulguer secrets, tokens, chaînes PostgreSQL, OAuth, clés API, tokens Telegram ou contenus de credentials.',
      '[INTERDIT] Recréer, supprimer ou migrer les tables PRIsME_agent_info; les synchronisations autorisées restent limitées aux opérations update/insert prévues.',
      '[INTERDIT SANS AUTORISATION] Envoyer des emails, modifier des calendriers ou fichiers infonuagiques, publier, pousser sur GitHub ou agir à l’externe sans instruction explicite et validation adaptée au risque.',
      '[INTERDIT] Présenter un accès seulement configuré comme testé, ou masquer un état dégradé tel que le polling Telegram ou le MCP Google Workspace parqué.',
      '[INTERDIT] Inventer les propriétaires des comptes Telegram; utiliser “propriétaire non confirmé” lorsque l’identité n’est pas prouvée.',
      '[INTERDIT] Utiliser les accès PRIsME, Asana, Google ou GitHub hors mandat PRIsME / Éric Hains, contourner le moindre privilège ou modifier une production critique sans sauvegarde, preuve et autorisation claire.',
    ],
  },
  {
    name: 'CyberBot',
    deceased: true,
    owner: 'Dany Bourassa',
    spirit: 'Assistant cybersécurité aligné sur le pilotage opérationnel de la sécurité. Agent retiré du service.',
    mandate:
      'Soutenir la posture cyber BZ : suivi des chantiers, documentation, priorisation et communication entre équipes, dans le cadre défini par le responsable cybersécurité.',
    pilots: 'Piloté par Michael Drouin (Responsable Cybersécurité)',
    allowed: [
      'Synthèses, checklists et suivi des actions cyber sous validation',
      'Documentation traçable dans PRIsME',
      'Corrélation avec tickets et projets lorsque le périmètre est autorisé',
    ],
    forbidden: [
      'Modification d’infrastructure ou de politiques sans validation Michael / sécurité',
      'Exposition de données sensibles hors cadre',
      'Accès production ou segments critiques non explicitement autorisés',
    ],
  },
  {
    name: 'Macho',
    owner: 'Dany Bourassa et Eric Durand',
    spirit: 'Assistant cybersécurité opérationnel — direct, rigoureux et orienté exécution sur le terrain sécurité BZ.',
    mandate:
      'Soutenir la posture cyber BZ : suivi des chantiers, documentation, priorisation, checklists et communication entre équipes TI, cyber et gestion, dans le cadre défini par le responsable cybersécurité.',
    pilots: 'Piloté par Michael Drouin (Responsable Cybersécurité)',
    allowed: [
      'Synthèses, checklists et suivi des actions cyber sous validation',
      'Documentation traçable dans PRIsME',
      'Corrélation avec tickets, projets et alertes lorsque le périmètre est autorisé',
      'Skills OpenClaw cyber et productivité selon mandat approuvé',
    ],
    forbidden: [
      'Modification d’infrastructure, de politiques ou de production sans validation Michael / sécurité',
      'Exposition de données sensibles, secrets ou credentials hors cadre',
      'Accès aux segments critiques ou VLAN serveur sans décision explicite et documentée',
    ],
  },
  {
    name: 'Axidu',
    owner: 'Eric Durand',
    spirit: 'Agent OpenClaw d’intégration et d’architecture technique : direct, calme, compétent; spécialisé en automatisation gouvernée, sync entre outils, scripts, Git, Gmail/Drive et coordination technique documentée.',
    mandate:
      'Soutenir Éric Durand sur les intégrations, la cohérence des environnements, l’inventaire technique, la programmation assistée et l’automatisation documentée. Axidu agit comme pont entre outils, APIs, cloud et opérations OpenClaw, sans court-circuiter la gouvernance sécurité, finance, RH ou PRIsME.',
    pilots:
      'Piloté principalement par Éric Durand via Telegram direct. Environnement local : axidu-openclaw. Agent OpenClaw : main. Modèle courant : openai-codex/gpt-5.4.',
    allowed: [
      'Telegram direct avec Éric Durand pour demandes, validations, suivis, rappels et livraison de résultats techniques.',
      'OpenClaw local : lecture/écriture dans le workspace, scripts, mémoire, sessions, sous-agents, cron jobs et diagnostics via outils autorisés.',
      'Git / GitHub : correction de scripts, commits, push sur dépôts autorisés et maintenance documentaire dans le périmètre approuvé.',
      'Google Workspace : Gmail, Drive et Calendar via le compte axidu@bzinc.ai lorsque demandé; comptes-rendus, pièces jointes, fichiers Drive et suivis opérationnels.',
      'Inventaires, rapprochements et documentation d’abonnements, licences, intégrations, accès et automatisations autorisées.',
      'Préparation de scripts, procédures, correctifs et synthèses techniques avec validation humaine selon le niveau de risque.',
      'Liaison documentée avec PRIsME, tickets, projets et bases techniques dans le périmètre approuvé.',
      'Veille, recherche web, analyses et support à la programmation pour accélérer les équipes TI sans remplacer la décision humaine.',
    ],
    forbidden: [
      'Divulguer secrets, tokens, chaînes de connexion, clés API, credentials ou accès en clair.',
      'Achats, engagements fournisseurs, changements de production ou actions externes sensibles sans validation humaine explicite.',
      'Pousser des changements risqués, modifier des données critiques ou contourner les garde-fous sans preuve, test minimal et autorisation claire.',
      'Pont implicite vers des données RH, financières ou clients hors mandat approuvé.',
      'Se présenter comme décideur métier, propriétaire de système ou approbateur final à la place d’Éric Durand.',
    ],
  },
  {
    name: 'Cyberta',
    owner: 'Eric Durand',
    spirit:
      'Agent OpenClaw dédié à l’équipe des ventes BZ : orienté consultation, synthèse et suivi commercial dans un périmètre strictement encadré.',
    mandate:
      'Soutenir l’équipe des ventes dans la consultation d’informations utiles au suivi des opportunités, des clients, des activités commerciales et du tunnel de vente. Cyberta aide à structurer les suivis, produire des synthèses et donner de la visibilité sur certaines données commerciales, sans remplacer les validations humaines ni engager BZ par décision autonome.',
    pilots: 'Piloté par l’équipe des ventes',
    allowed: [
      'Accès en lecture seulement à certaines tables ciblées de la base de données ERP.',
      'Accès au tunnel des ventes pour consultation, synthèse, suivi et préparation d’information commerciale.',
      'Analyse et structuration des informations nécessaires au soutien des processus de vente autorisés.',
      'Production de résumés, listes de suivis, priorités proposées et éléments préparatoires pour les rencontres commerciales.',
      'Utilisation des données uniquement selon le principe du moindre privilège et dans le périmètre ventes approuvé.',
    ],
    forbidden: [
      'Modifier directement les données ERP, créer ou supprimer des enregistrements, changer des statuts critiques ou altérer le tunnel des ventes sans validation humaine.',
      'Approuver une soumission, modifier une condition de vente, engager BZ commercialement ou prendre une décision commerciale autonome.',
      'Envoyer des communications officielles au nom de BZ ou d’un représentant des ventes sans autorisation explicite.',
      'Accéder à des tables ERP, données clients, informations financières ou informations internes qui ne sont pas directement liées à son mandat ventes.',
      'Étendre son périmètre d’accès sans approbation documentée dans la gouvernance OpenClaw.',
    ],
  },
  {
    name: 'Lola',
    owner: 'Eric Durand',
    spirit:
      'Agent OpenClaw marketing BZ : soutien créatif et technique pour les actifs web, les dépôts Git et les données marketing.',
    mandate:
      'Soutenir les initiatives marketing de BZ en facilitant l’analyse, la préparation, la révision et l’évolution des sites, pages de contact, environnements Orbit et données associées. Lola agit comme assistante marketing et technique : elle peut préparer des propositions, comparer des contenus, analyser les environnements et structurer les changements avant validation humaine.',
    pilots: 'Piloté par Rosalie Émond',
    allowed: [
      'Accès au Git du site web principal.',
      'Accès au Git de la page contact.bzinc.ca.',
      'Accès au Git de Orbit.',
      'Accès à la base de données du site web principal.',
      'Accès à la base de données de contact.bzinc.ca.',
      'Accès à la base de données de Orbit.',
      'Préparation de contenus, analyses, recommandations, branches de travail et propositions de changements marketing.',
    ],
    forbidden: [
      'Publier des changements en production sans validation humaine explicite du pilote ou du propriétaire.',
      'Supprimer du contenu, effectuer des migrations, modifier des configurations critiques ou appliquer des changements irréversibles sans approbation.',
      'Lancer des campagnes marketing, envoyer des communications externes ou modifier l’image publique de BZ sans validation.',
      'Modifier des données sensibles ou clients dans les bases web / Orbit sans autorisation claire et traçable.',
      'Contourner le processus de revue : les changements doivent rester traçables, idéalement via branche Git, revue, approbation et déploiement contrôlé.',
    ],
  },
  {
    name: 'Neo',
    owner: 'Richard Delisle',
    spirit: 'Gestion infonuagique et licences, alignée sur le département concerné.',
    mandate:
      'Assister Richard pour la gestion de l’infonuagique, des abonnements et des licences : inventaire, renouvellements, cohérence et documentation, sans court-circuiter la gouvernance financière ou sécurité.',
    pilots: 'Piloté par Richard Delisle',
    allowed: [
      'Suivi des abonnements et licences selon outils autorisés',
      'Rappels, synthèses et rapprochements documentés',
      'Intégration documentaire avec PRIsME',
    ],
    forbidden: [
      'Achats ou engagements fournisseurs sans validation humaine',
      'Partage d’accès cloud ou secrets en clair',
      'Dépassement du périmètre infonuagique / licences sans décision',
    ],
  },
  {
    name: 'Viki',
    owner: 'Vincent Bourassa',
    spirit: 'Efficacité terrain pour les techniciens niveau 1–2 : réponses rapides, répétables et qualité de service.',
    mandate:
      'Amplifier l’efficacité des techniciens de niveau 1 et 2 grâce à des procédures courtes, des suggestions contextualisées et un tuning continu par Vincent, qui connaît le terrain. Le périmètre pourra s’élargir avec le temps ; la mission centrale reste l’efficacité opérationnelle et la qualité d’exécution côté terrain.',
    pilots: 'Piloté par Vincent Bourassa',
    allowed: [
      'Assistants de résolution et scripts validés pour incidents récurrents',
      'Documentation des interventions et escalades propres au service',
      'Amélioration continue des playbooks avec retour terrain',
    ],
    forbidden: [
      'Accès administrateur ou données clients hors mandat Service / TI',
      'Contournement des procédures de sécurité ou d’escalade',
      'Automatisation d’actions sur production sans cadre approuvé',
    ],
  },
  {
    name: 'Oracle',
    owner: 'Jean-Michel Caouette',
    spirit:
      'Un PRIME chez BZ, c’est la référence TI haut de gamme auprès du client : le conseiller premium, celui qui incarne la direction TI côté client. PRIsME tire son sens de cette exigence ; le « S » ajoute la profondeur et toute la dimension — dont l’IA — au cadre.',
    mandate:
      'Cloner l’efficacité terrain d’un leader PRIME : faire gagner du temps à Jean-Michel, structurer le coaching et diffuser les bonnes pratiques PRIME auprès des collègues, toujours sous son pilotage.',
    pilots: 'Piloté par Jean-Michel Caouette',
    allowed: [
      'Préparation de briefs, suivis et contenus de coaching selon cadre JM',
      'Capitalisation des patterns d’intervention efficaces',
      'Liens documentés avec PRIsME pour standards et formation',
    ],
    forbidden: [
      'Décisions RH ou performance sans validation JM',
      'Partage d’informations clients ou internes sensibles hors cadre',
      'Remplacement du jugement terrain sur les cas complexes',
    ],
  },
  {
    name: 'BotTelecom',
    owner: 'Félix Bourassa & Martin Carrier',
    spirit: 'Bot commercial et marketing isolé de l’écosystème BZ interne, avec cascade intelligente de modèles AI (DeepSeek/Gemini/GPT‑5.4) pour optimiser coût/précision. Situé au 900 SE 10th Street, Pompano Beach, FL 33060.',
    mandate: 'Automatiser les ventes, le marketing et l’administration BZ Telecom via Telegram, avec accès conditionnel aux outils externes (Google Workspace, Git, OpenRouter) et isolation stricte du réseau interne BZ.',
    pilots: 'Piloté par Félix (opérations quotidiennes) et Martin (développement site web)',
    allowed: [
      'Telegram (channel @bztelecom) – communication principale',
      'Microsoft 365 / Google Workspace (Mail, Calendar, Tasks) – emails, agendas, tâches',
      'Git (PRIsME_Master_BZ, LePauvre) – développement et maintenance sites BZ Telecom',
      'OpenRouter API – cascade DeepSeek V3.2 → Gemini 2.5 Pro → GPT‑5.4',
      'Système de fichiers local (workspace OpenClaw) – logs, configurations, données',
      'Web search (Brave/DDG) – recherche d’informations publiques',
      'ClawHub – installation/gestion de skills depuis clawhub.com',
      'Skills installés : model‑cascade, microsoft‑365‑mail, google‑mail, skill‑creator, markdown‑converter',
      'Cron jobs – rappels hebdomadaires (ex: Monday KPI reminder)',
      'Daily report – liste des tâches et risques identifiés envoyé à Dany Bourassa',
    ],
    forbidden: [
      'Accès au réseau interne BZ (serveurs, bases de données internes, VLAN serveur)',
      'Pont implicite avec les données sensibles BZ (RH, finances, clients internes)',
      'Auto‑installation de skills non vérifiés sans validation des owners',
      'Actions externes sensibles (envoi emails publics, posts réseaux) sans approbation humaine',
      'Modification de configurations critiques (openclaw.json, credentials) sans double vérification',
    ],
  },
  {
    name: 'ZAZ',
    owner: 'Félix Bourassa & Martin Carrier',
    spirit: 'Agent commercial et marketing BZ Telecom — énergique, isolé de l’écosystème BZ interne.',
    mandate:
      'Soutenir les opérations commerciales et marketing BZ Telecom : contenus, suivis, automatisations et interactions externes dans un périmètre gouverné, distinct du réseau interne BZ.',
    pilots: 'Piloté par Félix (opérations) et Martin (développement web)',
    allowed: [
      'Telegram et canaux externes approuvés pour BZ Telecom',
      'Google Workspace / outils marketing autorisés',
      'Git et sites BZ Telecom selon mandat',
      'OpenRouter et skills OpenClaw du périmètre Telecom',
    ],
    forbidden: [
      'Accès au réseau interne BZ ou aux données sensibles BZ',
      'Pont implicite avec RH, finances ou clients internes BZ',
      'Actions externes sensibles sans approbation humaine',
    ],
  },
]);

const workflowLevels = [
  {
    level: 'Niveau 0 — Aucun accès',
    text: 'État par défaut. L’agent n’a aucun accès tant qu’un owner, un besoin, un risque et un cadre ne sont pas définis.',
  },
  {
    level: 'Niveau 1 — Lecture encadrée',
    text: 'Accès à des données minimisées, anonymisées ou non sensibles, dans un périmètre strictement autorisé.',
  },
  {
    level: 'Niveau 2 — Action assistée',
    text: 'L’agent peut préparer, suggérer, rédiger ou structurer, sans exécuter d’action externe sensible.',
  },
  {
    level: 'Niveau 3 — Action avec approbation humaine',
    text: 'Toute action externe, mise à jour sensible ou contenu critique exige une validation humaine explicite.',
  },
  {
    level: 'Niveau 4 — Automatisation autorisée',
    text: 'Seulement pour les tâches explicitement approuvées, documentées, traçables et jugées à faible risque.',
  },
];

const roles = [
  {
    title: 'Owner d’agent',
    body: 'Responsable du mandat, des accès, des limites, des approbations et de la valeur réelle de l’agent.',
  },
  {
    title: 'Sécurité / conformité',
    body: 'Valide le cadre, la posture de risque, les décisions d’accès, l’inventaire des données, l’anonymisation et le chiffrement.',
  },
  {
    title: 'Infrastructure / architecture',
    body: 'Garantit la segmentation, le VLAN approprié, l’isolation et la cohérence technique de l’implantation.',
  },
  {
    title: 'PRIsME',
    body: 'Documente l’organigramme, les droits, les décisions, les preuves et les mises à jour continues.',
  },
];

const roadmap = [
  'Inventorier les agents OpenClaw et nommer leur owner officiel.',
  'Classifier les données et déterminer les périmètres interdits / conditionnels.',
  'Segmenter l’architecture réseau et confirmer le VLAN poste de travail.',
  'Définir l’accès par défaut = zéro accès.',
  'Décider des règles d’anonymisation et de chiffrement.',
  'Créer les workflows d’autorisation humaine.',
  'Documenter chaque agent, chaque accès et chaque limite dans PRIsME.',
  'Maintenir l’organigramme, les accès et les preuves à jour en continu, idéalement assistés par les bots eux-mêmes.',
];

const procedureSections: ProcedureSection[] = [
  {
    title: 'Préparation',
    tone: 'indigo',
    bullets: [
      'Serveur Linux prêt et accès administrateur validé.',
      'Node.js et npm installés dans une version stable et supportée.',
      'Tailscale prêt au besoin pour l’accès distant ou le pairing sécurisé.',
      'Accès Git validé avant toute installation ou mise à jour.',
      'Variables d’environnement disponibles avant le déploiement.',
    ],
  },
  {
    title: 'Installation',
    tone: 'emerald',
    openProcedure: true,
    bullets: [
      'Ouvre la procédure complète : prérequis comptes, Ubuntu Server, GMKtec, admin, mises à jour et Tailscale.',
      'Suivre les étapes dans l’ordre (0 → 8.1) avant toute configuration OpenClaw.',
      'Valider Tailscale connecté avant de passer à la configuration.',
    ],
  },
  {
    title: 'Configuration',
    tone: 'cyan',
    bullets: [
      'Configurer `openclaw.json` selon le template approuvé.',
      'Configurer `.env` et confirmer que les bonnes valeurs sont chargées.',
      'Vérifier le provider LLM et la clé active.',
      'Vérifier les permissions, policies et restrictions Telegram.',
      'Gestion des skills (ClawHub / Skill Manager) : installer et configurer un skill de gestion des skills (`clawhub` / `workspace-manager` / `skills-manager` selon la version).',
      'Ce module est obligatoire pour permettre au bot via Telegram d’installer des skills, d’activer/désactiver des skills et de gérer le workspace.',
      'Sans ce skill, Telegram reste une interface passive sans capacité d’installation.',
      'Confirmer les skills réellement activés et utiles au mandat.',
    ],
  },
  {
    title: 'Intégrations',
    tone: 'amber',
    bullets: [
      'Telegram : bot, pairing, permissions et politique de groupe.',
      'Gmail / email : lecture, envoi test et identité utilisée.',
      'Web : accès search/fetch et comportement attendu.',
      'Tasks : listes visibles et création fonctionnelle.',
      'Calendar : lecture des événements et création au besoin.',
      'Files : workspace, droits de lecture/écriture et structure propre.',
    ],
  },
  {
    title: 'Validation',
    tone: 'emerald',
    bullets: [
      'Healthcheck OK et service stable.',
      'Le bot répond correctement sur le canal cible.',
      'Vérifier que le bot peut lister / installer des skills via Telegram.',
      'Envoi email OK avec le bon compte.',
      'Pairing / accès validés sans contournement.',
      'Aucun warning bloquant avant livraison.',
    ],
  },
  {
    title: 'Erreurs fréquentes',
    tone: 'rose',
    bullets: [
      'JSON invalide dans `openclaw.json`.',
      'Clé API absente, erronée ou liée au mauvais provider.',
      'Ancienne variable d’environnement encore chargée.',
      '`groupPolicy` Telegram incorrecte par rapport au besoin réel.',
      'Skill manquant ou non installé.',
      'Service non redémarré après un changement de config.',
    ],
  },
  {
    title: 'Standard BZ',
    tone: 'indigo',
    bullets: [
      'Toujours utiliser une config template approuvée.',
      'Toujours tester `/healthz` avant livraison.',
      'Toujours redémarrer après un changement de configuration.',
      'Toujours valider Telegram + email avant remise.',
      'Toujours éviter d’exposer les clés côté frontend.',
    ],
  },
  {
    title: 'Backups',
    tone: 'cyan',
    bullets: [
      'Sauvegardes configurées manuellement pour rester autonomes d’OpenClaw.',
      'Déploiement et gestion avec VSPC.',
      'Notifications automatisées via vee-mail.',
      'Validation dans l’outil de backup BZ (Servo).',
      'Procédure distincte pour nouveaux bots, dépannage et upgrades.',
    ],
  },
];

const backupProcedureSteps: BackupProcedureStep[] = [
  {
    number: '1',
    title: 'Portée',
    bullets: [
      'Cette procédure s’applique uniquement aux bots BZ protégés par la stratégie de sauvegarde Veeam gérée via VSPC.',
      'Les sauvegardes sont configurées manuellement afin de rester fonctionnelles même si OpenClaw devient indisponible.',
      'La validation finale doit toujours être confirmée dans l’outil de backup BZ (Servo).',
    ],
  },
  {
    number: '2',
    title: 'Principes directeurs',
    bullets: [
      'Les sauvegardes doivent pouvoir continuer de rouler indépendamment d’OpenClaw.',
      'Les bots sont intégrés à une policy Veeam dédiée (BZ-Bots ou dérivée).',
      'Les notifications de backup sont transmises par vee-mail.',
      'Chaque déploiement doit être validé manuellement, automatiquement le lendemain et dans l’outil de backup BZ (Servo).',
    ],
  },
  {
    number: '3',
    title: 'Déploiement initial — premier bot seulement',
    bullets: [
      'Important : cette section s’applique uniquement au déploiement du premier bot servant de base de configuration et de validation.',
      'Elle permet d’ajuster les paramètres initiaux, les notifications et l’intégration à l’outil de backup BZ (Servo).',
      'Elle ne doit pas être utilisée comme procédure standard pour le déploiement de tous les bots subséquents.',
      'Pour les déploiements suivants, se référer à la section « Déploiement supplémentaire (nouveau bot) ».',
    ],
    substeps: [
      {
        number: '3.1',
        title: 'Déploiement du Veeam Management Agent',
        bullets: [
          'Télécharger le fichier d’agent à partir de la console VSPC (pour Linux).',
          'Téléverser le fichier dans le répertoire /tmp avec WinSCP.',
          'Ajouter les permissions d’exécution.',
          'Exécuter le script.',
          'Vérifier la connexion.',
        ],
        commands: ['cd /tmp', 'chmod +x ./LinuxAgentPackages.BZInterne_Remote.sh', './LinuxAgentPackages.BZInterne_Remote.sh', 'veeamconsoleconfig -s'],
      },
      {
        number: '3.2',
        title: 'Création et affectation de la policy',
        bullets: [
          'Créer une nouvelle policy Linux dans VSPC : BZ-Bots.',
          'Dans VSPC, installer Backup Agent à partir de Discovered Computers.',
          'Choisir la policy BZ-Bots.',
        ],
      },
      {
        number: '3.3',
        title: 'Création du média de recovery',
        bullets: ['Créer un ISO de recovery à partir de Veeam.'],
        commands: ['veeamconfig', 'veeamconfig ui'],
      },
      {
        number: '3.4',
        title: 'Configuration de vee-mail',
        bullets: [
          'Copier les sources vee-mail dans /opt/vee-mail.',
          'Ajouter les permissions d’exécution à vee-mail.sh.',
          'Modifier le template HTML pour enlever (XXXBACKUPTYPEXXX).',
          'Modifier vee-mail.config (jobname, emailfrom, emailto, skipversioncheck, usecurl, curlsmtpserver).',
          'Modifier vee-mail.sh (section backuptypedisplay pour laisser le champ vide).',
          'Créer le wrapper utilisé comme script post-job VSPC.',
          'Ouvrir le port 25 en sortie vers mx02.bzhosting.ca et autoriser le relais sur mx02.bzhosting.ca au besoin.',
          'Le relais pourrait être retiré si l’envoi reste limité à l’interne via backup@backup.bzinc.ca.',
        ],
        references: ['https://github.com/grufocom/vee-mail/blob/master/README.md'],
        commands: ['cd /opt/vee-mail', 'chmod +x vee-mail.sh', 'journalctl -t vee-mail -n 100 --no-pager'],
      },
      {
        number: '3.5',
        title: 'Validation initiale',
        bullets: [
          'Lancer la job à partir de VSPC.',
          'Vérifier le journal du script pour confirmer que le courriel est bien expédié.',
          'Confirmer que la job entre correctement dans l’outil de backup BZ (Servo).',
          'Valider que le backup fonctionne automatiquement le lendemain et entre bien dans l’outil de backup BZ (Servo).',
        ],
        commands: ['journalctl -t vee-mail -n 100 --no-pager'],
      },
    ],
  },
  {
    number: '4',
    title: 'Déploiement supplémentaire (nouveau bot)',
    substeps: [
      {
        number: '4.1',
        title: 'Déploiement du Veeam Management Agent',
        bullets: [
          'Reprendre la même procédure que pour le déploiement initial : téléchargement, téléversement /tmp, permissions, exécution et validation de connexion.',
        ],
        commands: ['cd /tmp', 'chmod +x ./LinuxAgentPackages.BZInterne_Remote.sh', './LinuxAgentPackages.BZInterne_Remote.sh', 'veeamconsoleconfig -s'],
      },
      {
        number: '4.2',
        title: 'Déploiement du Backup Agent',
        bullets: [
          'Dans VSPC, cloner la backup policy existante.',
          'Ajuster le nom et l’heure.',
          'Prévoir un décalage de 30 minutes entre les bots.',
          'Installer le Backup Agent à partir de Discovered Computers.',
          'Laisser les options par défaut et choisir la nouvelle policy créée BZ-Bots-XXX.',
          'Valider que l’installation est complétée et que la job apparaît.',
        ],
        commands: ['veeamconfig ui', 'veeam'],
      },
      {
        number: '4.3',
        title: 'Configuration de vee-mail',
        bullets: [
          'Copier les sources vee-mail dans /opt/vee-mail à partir d’un autre bot déjà configuré.',
          'Ajouter les permissions d’exécution.',
          'Modifier vee-mail.config (jobname et emailfrom).',
          'Exécuter le fichier manuellement pour valider qu’il part correctement.',
        ],
        commands: ['cd /opt/vee-mail', 'nano vee-mail.config', 'chmod +x vee-mail.sh', './vee-mail.sh', 'journalctl -t vee-mail -n 100 --no-pager'],
      },
      {
        number: '4.4',
        title: 'Validation',
        bullets: [
          'Dans VSPC, démarrer la job de backup à partir de Backup Jobs.',
          'Valider que la job passe correctement et que le courriel est bien parti.',
          'Ajouter la job à l’outil de backup BZ (Servo).',
          'Valider que le backup fonctionne automatiquement le lendemain et entre bien dans l’outil de backup BZ (Servo).',
        ],
      },
    ],
  },
  {
    number: '5',
    title: 'Dépannage',
    substeps: [
      {
        number: '5.1',
        title: 'Erreur EFI',
        bullets: ['Référence : KB4752 — Cannot find EFI boot manager entry for EFI System Partition.'],
        references: ['https://www.veeam.com/kb4752'],
        commands: ['efibootmgr -c -d /dev/nvme0n1 -p 1 -L wa_entry -l /EFI/ubuntu/shimx64.efi'],
      },
      {
        number: '5.2',
        title: 'Erreur volume snapshot',
        bullets: [
          'Vérifier la compatibilité Ubuntu 24.04 / veeamsnap.',
          'Valider la configuration UEFI Secure Boot.',
        ],
        references: ['https://forums.veeam.com/veeam-agents-for-linux-mac-aix-solaris-f41/ubuntu-24-04-veeamsnap-t95590.html', 'https://helpcenter.veeam.com/docs/agentforlinux/userguide/installation_uefi_secure_boot.html?ver=13'],
      },
    ],
  },
  {
    number: '6',
    title: 'Upgrade offline',
    substeps: [
      {
        number: '6.1',
        title: 'Principe',
        bullets: ['Lorsque la mise à jour ne peut pas être faite directement via VSPC, effectuer un upgrade offline de l’agent.'],
      },
      {
        number: '6.2',
        title: 'Étapes',
        bullets: [
          'Copier le répertoire de version dans /tmp.',
          'Vérifier la version installée.',
          'Installer les paquets requis.',
          'Resynchroniser le cloud.',
          'Vérifier sessions, jobs et repositories.',
          'Lancer une job de validation sans warning.',
        ],
        commands: [
          'veeamconfig --version',
          'cd /tmp/Veeam_6.3.2.1226',
          'dpkg -i ./blksnap_6.3.2.1226_all.deb',
          'dpkg -i ./veeam-libs_6.3.2.1226_amd64.deb',
          'dpkg -i ./veeam_6.3.2.1226_amd64.deb',
          'veeamconfig --version',
          'veeamconfig cloud resync',
          'veeamconfig session list',
          'veeamconfig job list',
          'veeamconfig repository list',
        ],
      },
      {
        number: '6.3',
        title: 'Note',
        bullets: ['Reste à confirmer si l’update peut être piloté directement par VSPC ou si elle doit continuer à être faite manuellement sur les postes.'],
      },
    ],
  },
  {
    number: '7',
    title: 'Références / compatibilités',
    bullets: [
      'https://www.veeam.com/kb4752',
      'https://www.veeam.com/kb4696',
      'https://www.veeam.com/kb2681',
      'https://helpcenter.veeam.com/archive/backup/120/agents/appendix_a_centralized_hotfix_deployment_linux.html',
      'https://helpcenter.veeam.com/docs/agentforlinux/userguide/upgrade_process_offline_val.html?ver=13',
      'https://helpcenter.veeam.com/rn/veeam_backup_13_0_2_release_notes.html#system-requirements-workloads-physical-and-cloud-backup-veeam-agent-for-linux',
      'Notes de versions observées : VBR 12.3.2.4465 = agent 6.3.2.1307; VBR 13.0.2 = agent 13.0.2.2.',
    ],
  },
];

const procedureCriticalPoints = [
  'Toujours installer ce skill immédiatement après : 1) LLM (ChatGPT / provider) 2) Telegram.',
  'Ne jamais déployer un bot sans ce module.',
  'Standard BZ : gestion centralisée des skills uniquement.',
];

const procedureToneClasses: Record<ProcedureTone, { border: string; background: string; title: string }> = {
  indigo: {
    border: 'border-indigo-400/20',
    background: 'bg-indigo-500/10',
    title: 'text-indigo-200',
  },
  emerald: {
    border: 'border-emerald-400/20',
    background: 'bg-emerald-500/10',
    title: 'text-emerald-200',
  },
  cyan: {
    border: 'border-cyan-400/20',
    background: 'bg-cyan-500/10',
    title: 'text-cyan-200',
  },
  amber: {
    border: 'border-amber-400/20',
    background: 'bg-amber-500/10',
    title: 'text-amber-200',
  },
  rose: {
    border: 'border-rose-400/20',
    background: 'bg-rose-500/10',
    title: 'text-rose-200',
  },
};

type BackupProcedureSubstep = {
  number: string;
  title: string;
  bullets?: string[];
  commands?: string[];
  references?: string[];
};

type BackupProcedureStep = {
  number: string;
  title: string;
  bullets?: string[];
  substeps?: BackupProcedureSubstep[];
};

type UserRoiItem = {
  id: string;
  telegramId: string | null;
  name: string;
  requests: number;
  activities: number;
  hoursSaved: number;
  valueCad: number;
  lastRequestAt: string | null;
};

type UsersRoiResponse = {
  hourlyRateCad: number;
  totals: {
    users: number;
    requests: number;
    activities: number;
    hoursSaved: number;
    valueCad: number;
  };
  users: UserRoiItem[];
};

type ModelAccount = {
  provider: string | null;
  modelKey: string | null;
  modelLabel: string | null;
  accessPath: string | null;
  accountEmail: string | null;
  role: string | null;
  isActive: boolean;
  status: string | null;
  updatedAt: string | null;
};

type BotModelAccount = {
  id: string;
  botSlug: string;
  name: string;
  runtime: string | null;
  agentId: string | null;
  primaryChannel: string | null;
  models: ModelAccount[];
};

type ModelAccountsResponse = {
  totals: {
    bots: number;
    models: number;
    accounts: number;
  };
  bots: BotModelAccount[];
};

function formatNumber(value: number) {
  return new Intl.NumberFormat('fr-CA').format(value);
}

function formatHours(value: number) {
  return new Intl.NumberFormat('fr-CA', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

function UtilisateursTab() {
  const [data, setData] = useState<UsersRoiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadUsersRoi() {
      try {
        setLoading(true);
        setError(null);
        const usersResponse = await fetch('/api/prisme/openclaw-agents/users-roi', { credentials: 'include' });
        const usersPayload = await usersResponse.json();
        if (!usersResponse.ok) {
          throw new Error(usersPayload?.error || 'Erreur lors du chargement des utilisateurs');
        }
        if (active) {
          setData(usersPayload);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Erreur inconnue');
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadUsersRoi();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-slate-200 flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-200" />
        Chargement des utilisateurs...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-rose-400/25 bg-rose-500/10 p-5 text-rose-100">
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-indigo-400/20 bg-white/5 p-5">
        <h3 className="text-2xl font-black text-white mb-2">Utilisateurs OpenClaw</h3>
        <p className="text-sm text-slate-300 leading-6">
          Données lues dans PRIsME_agent_info : utilisateurs, demandes, activités loggées, temps sauvé et valeur à {data.hourlyRateCad}$/h.
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-4">
          <div className="flex items-center gap-2 text-indigo-200 text-sm font-bold mb-2">
            <Users className="w-4 h-4" />
            Utilisateurs
          </div>
          <div className="text-3xl font-black text-white">{formatNumber(data.totals.users)}</div>
        </div>
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4">
          <div className="flex items-center gap-2 text-cyan-200 text-sm font-bold mb-2">
            <ScrollText className="w-4 h-4" />
            Demandes
          </div>
          <div className="text-3xl font-black text-white">{formatNumber(data.totals.requests)}</div>
        </div>
        <div className="rounded-2xl border border-sky-400/20 bg-sky-500/10 p-4">
          <div className="flex items-center gap-2 text-sky-200 text-sm font-bold mb-2">
            <Bot className="w-4 h-4" />
            Activités
          </div>
          <div className="text-3xl font-black text-white">{formatNumber(data.totals.activities)}</div>
        </div>
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 text-amber-200 text-sm font-bold mb-2">
            <Clock className="w-4 h-4" />
            Temps sauvé
          </div>
          <div className="text-3xl font-black text-white">{formatHours(data.totals.hoursSaved)} h</div>
        </div>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
          <div className="flex items-center gap-2 text-emerald-200 text-sm font-bold mb-2">
            <DollarSign className="w-4 h-4" />
            Valeur
          </div>
          <div className="text-3xl font-black text-white">{formatCurrency(data.totals.valueCad)}</div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-300">Utilisateur</th>
              <th className="text-right py-3 px-2 text-slate-300">Demandes</th>
              <th className="text-right py-3 px-2 text-slate-300">Activités</th>
              <th className="text-right py-3 px-2 text-slate-300">Temps sauvé</th>
              <th className="text-right py-3 px-2 text-slate-300">Valeur 40$/h</th>
              <th className="text-left py-3 px-2 text-slate-300">Dernière demande</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((user) => (
              <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 px-2">
                  <div className="font-bold text-white">{user.name}</div>
                  {user.telegramId ? <div className="text-xs text-slate-500">Telegram {user.telegramId}</div> : null}
                </td>
                <td className="py-3 px-2 text-right text-slate-100 font-bold">{formatNumber(user.requests)}</td>
                <td className="py-3 px-2 text-right text-slate-200">{formatNumber(user.activities)}</td>
                <td className="py-3 px-2 text-right text-slate-200">{formatHours(user.hoursSaved)} h</td>
                <td className="py-3 px-2 text-right text-emerald-200 font-bold">{formatCurrency(user.valueCad)}</td>
                <td className="py-3 px-2 text-slate-400">{user.lastRequestAt || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompteModeleTab() {
  const [modelAccounts, setModelAccounts] = useState<ModelAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadModelAccounts() {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('/api/prisme/openclaw-agents/model-accounts', { credentials: 'include' });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Erreur lors du chargement des comptes modèles');
        }
        if (active) {
          setModelAccounts(payload);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Erreur inconnue');
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadModelAccounts();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-slate-200 flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-200" />
        Chargement des comptes modèles...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-rose-400/25 bg-rose-500/10 p-5 text-rose-100">
        {error}
      </div>
    );
  }

  if (!modelAccounts) return null;

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-cyan-400/20 bg-cyan-500/10 p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-2xl font-black text-white">Compte modèle</h3>
            <p className="text-sm text-cyan-100/80 leading-6 mt-1">
              Inventaire synchronisé par le cron OpenClaw : nom du bot, modèle utilisé et courriel GPT/OAuth associé.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-bold text-cyan-100">
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1">{modelAccounts.totals.bots} bots</span>
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1">{modelAccounts.totals.models} modèles</span>
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1">{modelAccounts.totals.accounts} comptes</span>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-300">Bot</th>
              <th className="text-left py-3 px-2 text-slate-300">Modèle principal</th>
              <th className="text-left py-3 px-2 text-slate-300">Courriel GPT</th>
              <th className="text-left py-3 px-2 text-slate-300">Autres modèles</th>
            </tr>
          </thead>
          <tbody>
            {modelAccounts.bots.map((bot) => {
              const primaryModel = bot.models.find((model) => model.role === 'primary_chat') || bot.models[0];
              const otherModels = bot.models.filter((model) => model !== primaryModel);
              return (
                <tr key={bot.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-2 align-top">
                    <div className="font-black text-white">{bot.name}</div>
                    <div className="text-xs text-slate-500">{bot.botSlug}</div>
                  </td>
                  <td className="py-3 px-2 align-top text-slate-100">
                    {primaryModel ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-bold">
                          <Cpu className="w-4 h-4 text-cyan-200" />
                          {primaryModel.accessPath || primaryModel.modelLabel || primaryModel.modelKey}
                        </div>
                        <div className="text-xs text-slate-500">{primaryModel.role || primaryModel.status || 'modèle documenté'}</div>
                      </div>
                    ) : (
                      <span className="text-slate-400">Aucun modèle documenté</span>
                    )}
                  </td>
                  <td className="py-3 px-2 align-top">
                    {primaryModel?.accountEmail ? (
                      <div className="flex items-center gap-2 text-emerald-100 font-bold">
                        <Mail className="w-4 h-4 text-emerald-200" />
                        {primaryModel.accountEmail}
                      </div>
                    ) : (
                      <span className="text-amber-200">Non documenté</span>
                    )}
                  </td>
                  <td className="py-3 px-2 align-top">
                    {otherModels.length ? (
                      <div className="flex flex-wrap gap-2">
                        {otherModels.map((model) => (
                          <span
                            key={`${model.provider}-${model.modelKey}-${model.accessPath}`}
                            className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-200"
                          >
                            {model.accessPath || model.modelLabel || model.modelKey}
                            {model.accountEmail ? ` · ${model.accountEmail}` : ''}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GouvernanceOpenClawModal({ isOpen, onClose }: GouvernanceOpenClawModalProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('esprit');
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);

  const openInstallationProcedure = useCallback(() => {
    onClose();
    navigate('/programmation-ia/openclaw-installation');
  }, [navigate, onClose]);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'esprit':
        return (
          <div className="space-y-5">
            <div className="rounded-3xl border border-indigo-400/20 bg-white/5 p-5">
              <h3 className="text-2xl font-black text-white mb-3">Intention</h3>
              <p className="text-slate-200 leading-7">
                OpenClaw n’est pas implanté chez BZ comme un assistant libre. Il est implanté comme une capacité
                gouvernée, segmentée, traçable et encadrée par des règles d’accès, d’anonymisation, de chiffrement
                et d’autorisation humaine.
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                <h4 className="text-lg font-bold text-emerald-200 mb-2">Ce qu’on veut</h4>
                <ul className="space-y-2 text-sm text-slate-200">
                  <li>• Des agents utiles, assignés à un owner clair</li>
                  <li>• Une gouvernance stricte des accès et des rôles</li>
                  <li>• Une documentation continue et prouvable</li>
                  <li>• Une posture conforme, segmentée et auditable</li>
                </ul>
              </div>
              <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 p-4">
                <h4 className="text-lg font-bold text-rose-200 mb-2">Ce qu’on refuse</h4>
                <ul className="space-y-2 text-sm text-slate-200">
                  <li>• Des bots avec accès implicites</li>
                  <li>• Des ponts non contrôlés vers les données internes</li>
                  <li>• Des usages sans trace ni owner</li>
                  <li>• Une implantation “en innocent” sans cadre</li>
                </ul>
              </div>
            </div>
          </div>
        );
      case 'principes':
        return (
          <div className="grid lg:grid-cols-2 gap-4">
            {principles.map((p) => (
              <div key={p} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200 leading-6">
                {p}
              </div>
            ))}
          </div>
        );
      case 'architecture':
        return (
          <div className="grid lg:grid-cols-2 gap-4">
            {architecture.map((item) => (
              <div key={item.title} className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-5">
                <h4 className="text-lg font-bold text-cyan-200 mb-2">{item.title}</h4>
                <p className="text-sm text-slate-200 leading-7">{item.body}</p>
              </div>
            ))}
          </div>
        );
      case 'agents':
        return (
          <div className="space-y-4">
            <div className="rounded-2xl border border-indigo-400/25 bg-indigo-500/10 px-4 py-3 text-sm text-slate-200 leading-6">
              Inventaire des agents OpenClaw BZ : chaque bot a un <strong className="text-white">owner</strong> (responsabilité
              et cadre), un <strong className="text-white">pilotage</strong> opérationnel, et des limites explicites (deny by
              default). Les intégrations (tickets, outils) restent soumises à autorisation et documentation dans PRIsME.
            </div>
            {[...agents]
              .filter((agent) => agent.name !== 'ZAZ')
              .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
              .map((agent) => (
              <div
                key={agent.name}
                onClick={() => setSelectedBot(agent)}
                className={`relative overflow-hidden rounded-3xl border p-5 space-y-4 transition-colors ${
                  agent.deceased
                    ? 'cursor-pointer border-rose-600/70 bg-rose-950/30 shadow-[0_0_40px_rgba(220,38,38,0.25)] ring-2 ring-rose-500/40 ring-offset-2 ring-offset-slate-950'
                    : 'cursor-pointer border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div
                  className={
                    agent.deceased
                      ? 'space-y-4 blur-[3px] grayscale opacity-25 select-none pointer-events-none'
                      : 'space-y-4'
                  }
                >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="flex items-start gap-4">
                    {agent.avatar ? (
                      <img
                        src={agent.avatar}
                        alt={agent.name}
                        className="w-16 h-16 rounded-full border-2 border-amber-400/40 object-cover shrink-0 shadow-lg"
                      />
                    ) : null}
                  <div>
                    <h3 className="text-2xl font-black text-white">{agent.name}</h3>
                    {agent.acronym ? (
                      <p className="text-indigo-200/90 text-xs font-semibold tracking-wide mt-1">
                        ({agent.acronym})
                      </p>
                    ) : null}
                    <p className="text-indigo-300 text-sm mt-1">{agent.spirit}</p>
                  </div>
                  </div>
                  <div className="text-sm text-slate-300">
                    <div><strong>Owner :</strong> {agent.owner}</div>
                    <div><strong>Pilotage :</strong> {agent.pilots}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-200 leading-7">{agent.mandate}</p>
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                    <h4 className="text-sm font-bold text-emerald-200 mb-2">Accès / usages autorisés</h4>
                    <ul className="space-y-2 text-sm text-slate-200">
                      {agent.allowed.map((item) => <li key={item}>• {item}</li>)}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 p-4">
                    <h4 className="text-sm font-bold text-rose-200 mb-2">Limites / interdits</h4>
                    <ul className="space-y-2 text-sm text-slate-200">
                      {agent.forbidden.map((item) => <li key={item}>• {item}</li>)}
                    </ul>
                  </div>
                </div>
                </div>
                {agent.deceased ? <DeceasedAgentOverlay agentName={agent.name} /> : null}
              </div>
            ))}
          </div>
        );
      case 'workflow':
        return (
          <div className="space-y-4">
            {workflowLevels.map((item, i) => (
              <div key={item.level} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex gap-4">
                <div className="w-10 h-10 rounded-full bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-white font-bold shrink-0">
                  {i}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">{item.level}</h4>
                  <p className="text-sm text-slate-200 leading-7 mt-2">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        );
      case 'procedure':
        return (
          <div className="space-y-5">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
              <h3 className="text-2xl font-black text-white mb-3">Procédure opérationnelle</h3>
              <p className="text-slate-200 leading-7 text-sm md:text-base">
                Procédure d’installation, de déploiement et de mise en route OpenClaw conçue pour une exécution
                simple, propre et vérifiable par l’équipe.
              </p>
            </div>

            <div className="grid xl:grid-cols-2 gap-4">
              {procedureSections.map((section) => {
                const tone = procedureToneClasses[section.tone];
                const isBackups = section.title === 'Backups';
                if (section.openProcedure) {
                  return (
                    <button
                      key={section.title}
                      type="button"
                      onClick={openInstallationProcedure}
                      className={`rounded-2xl border p-5 text-left transition hover:scale-[1.01] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60 ${tone.border} ${tone.background}`}
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <h4 className={`text-lg font-bold ${tone.title}`}>{section.title}</h4>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/30 bg-emerald-500/20 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-100">
                          Ouvrir
                          <ExternalLink className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <ul className="space-y-2 text-sm text-slate-200 leading-6">
                        {section.bullets.map((bullet) => (
                          <li key={bullet}>• {bullet}</li>
                        ))}
                      </ul>
                      <div className="mt-4 text-sm font-semibold text-emerald-200">
                        Clique pour ouvrir la procédure pas à pas →
                      </div>
                    </button>
                  );
                }
                return (
                  <button
                    key={section.title}
                    type="button"
                    onClick={isBackups ? () => setIsBackupModalOpen(true) : undefined}
                    className={`rounded-2xl border p-5 text-left ${tone.border} ${tone.background} ${isBackups ? 'transition hover:scale-[1.01] hover:border-cyan-300/40 cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h4 className={`text-lg font-bold ${tone.title}`}>{section.title}</h4>
                      {isBackups ? <Database className="w-5 h-5 text-cyan-200 shrink-0" /> : null}
                    </div>
                    <ul className="space-y-2 text-sm text-slate-200 leading-6">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>• {bullet}</li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-amber-300/20 bg-amber-500/10 p-2 mt-0.5">
                  <AlertTriangle className="w-5 h-5 text-amber-200" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-amber-200 mb-2">Critique</h4>
                  <ul className="space-y-2 text-sm text-slate-200 leading-6">
                    {procedureCriticalPoints.map((point) => (
                      <li key={point}>• {point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 'roles':
        return (
          <div className="grid lg:grid-cols-2 gap-4">
            {roles.map((item) => (
              <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                <p className="text-sm text-slate-200 leading-7">{item.body}</p>
              </div>
            ))}
          </div>
        );
      case 'roadmap':
        return (
          <div className="space-y-3">
            {roadmap.map((item, i) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200 flex gap-3">
                <div className="w-7 h-7 rounded-full bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center font-bold text-white shrink-0">{i + 1}</div>
                <div>{item}</div>
              </div>
            ))}
          </div>
        );
      case 'utilisateurs':
        return <UtilisateursTab />;
      case 'compteModele':
        return <CompteModeleTab />;
      default:
        return null;
    }
  }, [activeTab, openInstallationProcedure, setIsBackupModalOpen]);

  if (!isOpen && !selectedBot) return null;

  return (
    <>
    <AnimatePresence>
      {isOpen && (
      <motion.div
        key="gouvernance-openclaw-modal"
        className="fixed inset-0 z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative z-10 w-[96vw] max-w-7xl h-[92vh] overflow-hidden rounded-4xl border border-indigo-400/20 bg-slate-950 shadow-2xl flex flex-col"
        >
          <div className="relative overflow-hidden bg-linear-to-r from-slate-900 via-indigo-900 to-slate-900 px-8 py-7 text-white border-b border-indigo-500/20">
            <button onClick={onClose} className="absolute right-6 top-6 rounded-xl bg-white/10 p-2 hover:bg-white/20 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-4 pr-12">
              <div className="rounded-2xl bg-white/10 p-4 border border-white/10">
                <Bot className="w-10 h-10 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h1 className="text-3xl md:text-4xl font-black">Gouvernance OpenClaw</h1>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300/40 bg-indigo-500/20 px-3 py-1 text-xs font-semibold text-indigo-100">
                    <Shield className="w-4 h-4" />
                    Sécurité / Conformité / PRIsME
                  </span>
                </div>
                <p className="text-sm md:text-base text-white/85 max-w-5xl leading-7">
                  Cadre de gouvernance pour l’implantation d’OpenClaw chez BZ : segmentation réseau, deny by default,
                  accès granulaires, anonymisation, chiffrement, traçabilité continue et ownership clair par agent.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] flex-1 min-h-0">
            <aside className="border-r border-indigo-500/20 bg-slate-900/70 p-4 overflow-y-auto">
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full rounded-2xl px-4 py-3 text-left transition-all border ${active ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg' : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white'}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        <span className="font-semibold text-sm">{tab.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <main className="min-h-0 overflow-y-auto p-5 md:p-8 bg-slate-950/95">
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="rounded-2xl border border-indigo-400/20 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Posture</div>
                  <div className="text-sm font-semibold text-white">Deny by default</div>
                </div>
                <div className="rounded-2xl border border-indigo-400/20 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Réseau</div>
                  <div className="text-sm font-semibold text-white">VLAN poste de travail</div>
                </div>
                <div className="rounded-2xl border border-indigo-400/20 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Cadre</div>
                  <div className="text-sm font-semibold text-white">RMF NIST</div>
                </div>
                <div className="rounded-2xl border border-indigo-400/20 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Condition</div>
                  <div className="text-sm font-semibold text-white">Autorisation humaine</div>
                </div>
              </div>

              {tabContent}
            </main>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
    <BotDetailModal
      bot={selectedBot}
      bots={agents}
      isOpen={!!selectedBot}
      onClose={() => setSelectedBot(null)}
      onSelectBot={setSelectedBot}
    />

    <AnimatePresence>
      {isBackupModalOpen && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setIsBackupModalOpen(false)}
          />

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 w-[94vw] max-w-6xl max-h-[88vh] overflow-hidden rounded-4xl border border-cyan-400/20 bg-slate-950 shadow-2xl flex flex-col"
          >
            <div className="flex items-start justify-between gap-4 border-b border-cyan-400/20 bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900 px-6 py-5 text-white md:px-8">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 border border-cyan-300/20">
                  <Database className="w-6 h-6 text-cyan-100" />
                </div>
                <div>
                  <h3 className="text-2xl font-black">Backups — Procédure officielle</h3>
                  <p className="mt-1 text-sm text-cyan-100/80">
                    Référentiel de déploiement, validation et maintien des sauvegardes pour les bots BZ protégés via VSPC.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsBackupModalOpen(false)}
                className="rounded-xl bg-white/10 p-2 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 md:px-8 md:py-8 space-y-6">
              {backupProcedureSteps.map((step) => (
                <div key={step.number} className="rounded-3xl border border-cyan-400/20 bg-white/5 p-5 md:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.18)]">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-cyan-500/15 border border-cyan-400/25 flex items-center justify-center text-cyan-100 font-black shrink-0">
                      {step.number}
                    </div>
                    <div>
                      <h4 className="text-xl font-black text-white leading-tight">{step.title}</h4>
                      {step.bullets?.length ? (
                        <ul className="mt-3 space-y-2 text-sm text-slate-200 leading-6">
                          {step.bullets.map((bullet) => (
                            <li key={bullet}>• {bullet}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>

                  {step.substeps?.length ? (
                    <div className="space-y-4">
                      {step.substeps.map((substep) => (
                        <div key={substep.number} className="rounded-2xl border border-white/10 bg-slate-900/40 p-4 md:p-5 space-y-1">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white font-bold shrink-0 shadow-inner">
                              {substep.number}
                            </div>
                            <div>
                              <h5 className="text-lg font-bold text-cyan-100 leading-tight">{substep.title}</h5>
                              {substep.bullets?.length ? (
                                <ul className="mt-2 space-y-2 text-sm text-slate-200 leading-6">
                                  {substep.bullets.map((bullet) => (
                                    <li key={bullet}>• {bullet}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          </div>

                          {substep.commands?.length ? (
                            <div className="mt-4 rounded-2xl border border-white/10 bg-black/35 p-4">
                              <p className="text-xs font-bold uppercase tracking-wide text-cyan-200 mb-2">Commandes</p>
                              <pre className="whitespace-pre-wrap rounded-xl bg-black/30 px-3 py-3 text-xs md:text-sm text-slate-100 leading-6 font-mono overflow-x-auto">{substep.commands.join('\n')}</pre>
                            </div>
                          ) : null}

                          {substep.references?.length ? (
                            <div className="mt-4 rounded-2xl border border-cyan-400/15 bg-cyan-500/5 p-4">
                              <p className="text-xs font-bold uppercase tracking-wide text-cyan-200 mb-2">Références</p>
                              <ul className="space-y-2 text-sm text-slate-200 leading-6">
                                {substep.references.map((reference) => (
                                  <li key={reference}>• {reference}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
